import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../firebase';

let confirmationResult = null;
let recaptchaInitializationPromise = null;

const getCurrentHostname = () => {
  if (typeof window === 'undefined') return 'this website';
  return window.location.hostname || 'this website';
};

const getCurrentOrigin = () => {
  if (typeof window === 'undefined') return 'this website origin';
  return window.location.origin || 'this website origin';
};

export const normalizeIndianPhoneNumber = (value) => {
  const input = String(value || '').trim();
  if (!input || /[A-Za-z]/.test(input)) return '';
  const digits = input.replace(/\D/g, '').replace(/^91(?=[6-9]\d{9}$)/, '');
  return /^[6-9]\d{9}$/.test(digits) ? `+91${digits}` : '';
};

export const initializeRecaptchaVerifier = async () => {
  const container = document.getElementById('recaptcha-container');
  if (!container) {
    throw new Error('Firebase reCAPTCHA container is not available. Refresh the page and try again.');
  }

  const existingVerifier = window.recaptchaVerifier;
  const existingVerifierIsUsable = existingVerifier
    && existingVerifier.destroyed !== true
    && existingVerifier.container === container
    && document.documentElement.contains(container);

  if (!existingVerifierIsUsable) {
    window.recaptchaVerifier = new RecaptchaVerifier(
      'recaptcha-container',
      { size: 'invisible' },
      auth,
    );
    recaptchaInitializationPromise = null;
  }

  if (!recaptchaInitializationPromise) {
    const verifier = window.recaptchaVerifier;
    recaptchaInitializationPromise = verifier.render()
      .then(() => verifier)
      .catch((error) => {
        recaptchaInitializationPromise = null;
        throw error;
      });
  }

  return recaptchaInitializationPromise;
};

const resetRecaptchaVerifier = () => {
  const existingVerifier = window.recaptchaVerifier;
  recaptchaInitializationPromise = null;
  window.recaptchaVerifier = null;

  if (existingVerifier && existingVerifier.destroyed !== true) {
    try {
      existingVerifier.clear();
    } catch {
      // Ignore cleanup failures. A fresh verifier will be created on the next OTP attempt.
    }
  }
};

const getErrorText = (error) => {
  try {
    return `${error?.code || ''} ${error?.message || ''} ${JSON.stringify(error?.customData || {})}`;
  } catch {
    return `${error?.code || ''} ${error?.message || ''}`;
  }
};

export const getOtpErrorMessage = (error) => {
  const details = getErrorText(error);

  if (/API_KEY_SERVICE_BLOCKED|Identity Toolkit.*blocked|requests-to-this-api.*blocked/i.test(details)) {
    return 'Firebase Authentication is blocked for this API key. Allow the Identity Toolkit API in Google Cloud API key restrictions.';
  }

  const messages = {
    'auth/invalid-phone-number': 'Enter a valid 10-digit Indian mobile number.',
    'auth/too-many-requests': 'Too many attempts. Please wait before trying again.',
    'auth/quota-exceeded': 'The Firebase SMS quota has been reached.',
    'auth/billing-not-enabled': 'Real SMS delivery requires the Firebase Blaze billing plan.',
    'auth/sms-region-policy-denied': 'Allow India in Firebase Authentication SMS region policy.',
    'auth/captcha-check-failed': 'Security verification failed. Please try again.',
    'auth/network-request-failed': 'Network error. Check your connection and try again.',
    'auth/operation-not-allowed': 'Enable the Phone sign-in provider in Firebase Authentication.',
    'auth/app-not-authorized': `Add ${getCurrentHostname()} to Firebase Authentication authorized domains.`,
    'auth/invalid-app-credential': `Firebase rejected the reCAPTCHA response for ${getCurrentOrigin()}. Use http://127.0.0.1:3000 locally, or add this exact origin to the Firebase API key referrer allowlist.`,
    'auth/invalid-verification-code': 'The verification code is incorrect.',
    'auth/code-expired': 'The verification code expired. Request a new one.',
    'auth/internal-error': 'Firebase Authentication is not configured correctly for this website.',
  };

  return messages[error?.code] || 'Phone verification failed. Please try again.';
};

export const sendSmsOtp = async (phone) => {
  confirmationResult = null;

  try {
    const verifier = await initializeRecaptchaVerifier();
    confirmationResult = await signInWithPhoneNumber(auth, phone, verifier);
  } catch (error) {
    if (error?.code === 'auth/invalid-app-credential' || error?.code === 'auth/captcha-check-failed') {
      resetRecaptchaVerifier();
    }

    const publicError = new Error(getOtpErrorMessage(error));
    publicError.code = error?.code;
    throw publicError;
  }
};

export const verifySmsOtp = async (phone, code) => {
  if (!confirmationResult) {
    throw new Error('Request a verification code first.');
  }

  try {
    const credential = await confirmationResult.confirm(code);
    if (credential.user.phoneNumber !== phone) {
      throw new Error('The verified phone number does not match.');
    }

    confirmationResult = null;
    return credential.user;
  } catch (error) {
    const publicError = new Error(getOtpErrorMessage(error));
    publicError.code = error?.code;
    throw publicError;
  }
};
